import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import Chip from '../../../components/Chip';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import EmptyState from '../../../components/EmptyState';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
} from '../../../components/StandardTable';
import { RunIcon, WarningIcon } from '../../../components/icons';
import { HelpTrigger } from '../../../help';
import { formatDate } from '../../lib/format';

interface OpenOfficeElect {
  id: string;
  person_id: string;
  name: string;
  title: string;
  district_label: string | null;
  effective_date: string;
  term_end: string | null;
  result_status: 'unofficial' | 'certified' | null;
  cycle_confirmed: boolean | null;
  outgoing_name: string | null;
  re_elected: boolean;
}

interface TransitionRun {
  id: string;
  run_date: string;
  trigger: 'schedule' | 'manual';
  started_at: string;
  finished_at: string | null;
  error: string | null;
  summary: {
    promoted?: unknown[];
    errors?: { error: string }[];
    roster?: { session_key: string | null; activated: number; added: number };
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

/** Unofficial within 14 days of taking office (PRD §5.6). */
const needsCertification = (row: OpenOfficeElect) =>
  row.result_status !== 'certified'
  && new Date(`${row.effective_date}T00:00:00`).getTime() - Date.now() < 14 * DAY_MS;

const runSummary = (run: TransitionRun) => {
  if (run.error) return `Failed: ${run.error}`;
  const promoted = run.summary.promoted?.length ?? 0;
  const roster = run.summary.roster;
  const parts = [`${promoted} took office`];
  if (roster?.session_key) parts.push(`${roster.activated + roster.added} roster rows seated for ${roster.session_key}`);
  if (run.summary.errors?.length) parts.push(`${run.summary.errors.length} need attention`);
  return parts.join(' · ');
};

/**
 * Upcoming transitions (Documentation/ELECTION_OUTCOMES_PRD.md §5.8): every open
 * Office-elect role, the day they take office, who they succeed, and the daily job's
 * last run, with Run now.
 */
const OfficeTransitionsCard: React.FC = () => {
  const queryClient = useQueryClient();
  const [confirmRun, setConfirmRun] = useState(false);

  const transitions = useQuery({
    queryKey: ['admin-elections', 'transitions'],
    queryFn: async () => {
      const response = await api.get('/admin/elections/transitions');
      return response.data.data as { office_elects: OpenOfficeElect[]; last_run: TransitionRun | null };
    },
  });

  const runMutation = useMutation({
    mutationFn: () => api.post('/admin/elections/transitions/run', {}),
    onSuccess: (response) => {
      const promoted = response.data.data.promoted?.length ?? 0;
      toast.success(promoted ? `${promoted} Office-elect${promoted === 1 ? '' : 's'} took office` : 'Transition job ran — nothing was due');
      setConfirmRun(false);
      queryClient.invalidateQueries(['admin-elections']);
    },
    onError: (error) => {
      toast.error(apiError(error, 'The transition job failed'));
      setConfirmRun(false);
    },
  });

  const rows = transitions.data?.office_elects ?? [];
  const lastRun = transitions.data?.last_run ?? null;

  return (
    <div className="card p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Upcoming transitions</h4>
          <HelpTrigger contentKey="office-elect-transition" size="sm" ariaLabel="When an Office-elect takes office" />
        </span>
        <Button variant="outline" size="sm" icon={RunIcon} onClick={() => setConfirmRun(true)}>
          Run Now
        </Button>
      </div>
      <p className="mb-3 text-xs text-text-tertiary dark:text-text-dark-tertiary">
        Office-elects take office automatically just after midnight (Denver time) on their start day.
        {lastRun && <> Last run {formatDate(lastRun.started_at)} ({lastRun.trigger === 'manual' ? 'Run now' : 'scheduled'}): {runSummary(lastRun)}.</>}
      </p>

      {transitions.isError ? (
        <EmptyState tone="error" size="compact" title="Couldn't load transitions" />
      ) : rows.length === 0 && !transitions.isLoading ? (
        <EmptyState size="compact" title="No Office-elects waiting to take office"
          hint="Winners appear here once results are applied, until the day they take office." />
      ) : (
        <StandardTable>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell>Office-elect</StandardTableHeaderCell>
              <StandardTableHeaderCell>Office</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit">Takes office</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md">Succeeds</StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {rows.map((row) => (
              <StandardTableRow key={row.id}>
                <StandardTableCell>
                  {row.name}
                  {needsCertification(row) && (
                    <p className="mt-1 flex items-start gap-1 text-xs text-warning-700 dark:text-warning-400">
                      <WarningIcon className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                      Result still unofficial
                    </p>
                  )}
                </StandardTableCell>
                <StandardTableCell>
                  {row.title}{row.district_label ? `, ${row.district_label}` : ''}
                </StandardTableCell>
                <StandardTableCell>
                  <span className="whitespace-nowrap">{formatDate(row.effective_date)}</span>
                  {row.cycle_confirmed === false && (
                    <span className="ml-1"><Chip label="Expected" tone="neutral" size="sm" /></span>
                  )}
                </StandardTableCell>
                <StandardTableCell responsiveHide="md">
                  {row.re_elected ? 'Re-elected' : row.outgoing_name ?? '—'}
                </StandardTableCell>
              </StandardTableRow>
            ))}
          </StandardTableBody>
        </StandardTable>
      )}

      <ConfirmationDialog
        isOpen={confirmRun}
        title="Run the transition job now?"
        message="Office-elects whose start day has arrived take office, the officeholders they replace end, and the current session's roster is seated. It does what the midnight run would do, so nothing changes for anyone whose day hasn't come."
        confirmText="Run Now"
        variant="info"
        onConfirm={() => runMutation.mutate()}
        onCancel={() => setConfirmRun(false)}
      />
    </div>
  );
};

export default OfficeTransitionsCard;
